import type { FunctionComponent, InputHTMLAttributes } from 'react'
import { useRef } from 'react'
import styled from 'styled-components'
import Close from '../../assets/icons/close.svg'
import Search from '../../assets/icons/search.svg'
import Button, { Variant } from '../Button/Button'
import Icon from '../Icon/Icon'
import Input from '../Input/Input'

const StyledButton = styled(Button)`
  position: absolute;
  background: transparent;
  top: 50%;
  transform: translateY(-50%);
  margin-right: 0;
  &:hover {
    color: var(--blue);
    svg path {
      fill: var(--blue);
    }
  }
  &.close {
    right: 2.5rem;
    @media screen and (min-width: 48rem) {
      right: 1.75rem;
    }
    svg path {
      fill: var(--dark);
      &:hover {
        fill: var(--blue);
      }
    }
  }
  &.search {
    right: 0;
    svg path {
      fill: var(--dark);
      &:hover {
        fill: var(--blue);
      }
    }
  }
`

const StyledInput = styled(Input)`
  border: none;
  border-bottom: solid 2px #000;
  border-top: none;
  border-right: none;
  border-left: none;
  font-size: 1.125rem;
  line-height: 28px;
  padding: 0 0 0 2.25rem;
  height: var(--header-menu-height);

  ::placeholder {
    color: var(--dark);
  }

  @media screen and (min-width: 48rem) {
    padding: 0;
    height: 1.875rem;
  }
`

const SearchBarWrapper = styled.div`
  display: flex;
  position: relative;
  justify-content: center;
  align-items: center;
  margin: 0;
  width: 100%;

  button {
    flex-shrink: 0;
  }
  @media screen and (min-width: 48rem) {
    margin: 0 1rem 0 0.5rem;
    max-width: 500px;
  }

  &.legend {
    input {
      height: auto;
      line-height: 1.6;
      padding: 0.25rem 0;

      ::placeholder {
        color: var(--greyText);
      }
    }
    margin-bottom: 0.5rem;

    @media screen and (min-width: 48rem) {
      margin: 0;
    }
  }

  &.catalog {
    margin: 0;
    input {
      margin: 0;
      height: auto;
      line-height: 1.6;
      padding: 0.5rem 0;
      ::placeholder {
        color: var(--greyText);
      }
    }
    svg path {
      fill: var(--dark);
    }
  }
`

export interface SearchBarProps extends InputHTMLAttributes<HTMLInputElement> {
  onClear: () => void
  placeholder?: string
  iconSize?: number
  className?: string
  name?: string
  id?: string
}

const SearchBar: FunctionComponent<SearchBarProps> = ({
  onBlur,
  onFocus,
  onChange,
  onClear,
  onKeyDown,
  value,
  children,
  placeholder,
  iconSize,
  name,
  className,
  id,
}) => {
  const searchBarProps = {
    onBlur,
    onFocus,
    onKeyDown,
    onChange,
    value,
    id,
  }

  const searchInput = useRef<HTMLInputElement>(null)

  const focusInput = () => {
    if (searchInput.current !== null) {
      searchInput.current.focus()
    }
  }

  return (
    <SearchBarWrapper className={className || 'searchBarWrapper'}>
      <StyledInput
        placeholder={placeholder || 'Zoeken'}
        aria-label={placeholder || 'Zoeken'}
        data-testid="searchInput"
        inputRef={searchInput}
        type="text"
        name={name || 'search'}
        autoComplete="off"
        {...searchBarProps}
      />
      {onClear && value && (
        <StyledButton
          className="close"
          variant={Variant.TextButton}
          type="button"
          aria-label="Clear"
          onClick={() => {
            onClear()
            focusInput()
          }}
          size={iconSize || 16}
          data-testid="clearbutton"
        >
          <Icon size={iconSize || 16} color="var(--dark)">
            <Close />
          </Icon>
        </StyledButton>
      )}
      <StyledButton
        className="search"
        variant={Variant.TextButton}
        aria-label="Search"
        id="search"
        size={iconSize || 16}
        data-testid="searchBarButton"
      >
        <Icon size={iconSize || 16} color="var(--dark)">
          <Search />
        </Icon>
      </StyledButton>
      {children}
    </SearchBarWrapper>
  )
}

export default SearchBar
